import type {FilesRoutePayload} from '../page-data/payloads/files'
import {
  pullRequestsChangesRoute,
  pullRequestsChangesWithRangeRoute,
  pullRequestsFilesRoute,
  pullRequestsFilesWithRangeRoute,
} from '../routes/files-changed-routes'
import {useIsRouteMounted} from '@github-ui/react-core/future/use-is-route-mounted'
import {useChildRouteQuery, useRouteQuery} from '@github-ui/react-core/future/use-route-query'

/**
 * Component that fetches data from whichever files changed route is currently active.
 * Renders children with the route data from one of:
 * - pullRequestsChangesRoute
 * - pullRequestsChangesWithRangeRoute
 * - pullRequestsFilesRoute
 * - pullRequestsFilesWithRangeRoute
 */
export interface FilesChangedRouteDataFetcherProps {
  children: (data: FilesRoutePayload | undefined) => React.ReactNode
  /** When true, uses useChildRouteQuery instead of useRouteQuery */
  readFromChildRoute?: boolean
}

export function FilesChangedRouteDataFetcher({children, readFromChildRoute}: FilesChangedRouteDataFetcherProps) {
  return (
    <>
      <RouteDataFetcher<FilesRoutePayload>
        route={pullRequestsChangesRoute}
        queryName="mainQuery"
        readFromChildRoute={readFromChildRoute}
      >
        {children}
      </RouteDataFetcher>
      <RouteDataFetcher<FilesRoutePayload>
        route={pullRequestsChangesWithRangeRoute}
        queryName="mainQuery"
        readFromChildRoute={readFromChildRoute}
      >
        {children}
      </RouteDataFetcher>
      <RouteDataFetcher<FilesRoutePayload>
        route={pullRequestsFilesRoute}
        queryName="mainQuery"
        readFromChildRoute={readFromChildRoute}
      >
        {children}
      </RouteDataFetcher>
      <RouteDataFetcher<FilesRoutePayload>
        route={pullRequestsFilesWithRangeRoute}
        queryName="mainQuery"
        readFromChildRoute={readFromChildRoute}
      >
        {children}
      </RouteDataFetcher>
    </>
  )
}

/**
 * Generic component that safely fetches route data only when the route is mounted.
 * Uses a two-component pattern to satisfy React's rules of hooks:
 * - Outer component checks if route is mounted
 * - Inner component (only rendered when mounted) calls useRouteQuery
 */
interface RouteDataFetcherProps<TData> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  route: any
  queryName: string
  children: (data: TData | undefined) => React.ReactNode
  /** When true, uses useChildRouteQuery instead of useRouteQuery */
  readFromChildRoute?: boolean
}

function RouteDataFetcher<TData>({route, queryName, children, readFromChildRoute}: RouteDataFetcherProps<TData>) {
  const isMounted = useIsRouteMounted(route)
  if (!isMounted) return null
  if (readFromChildRoute) {
    return (
      <RouteDataFetcherInnerChild route={route} queryName={queryName}>
        {children}
      </RouteDataFetcherInnerChild>
    )
  }
  return (
    <RouteDataFetcherInner route={route} queryName={queryName}>
      {children}
    </RouteDataFetcherInner>
  )
}

function RouteDataFetcherInner<TData>({route, queryName, children}: RouteDataFetcherProps<TData>) {
  const {data} = useRouteQuery(route, queryName)
  return <>{children(data as TData | undefined)}</>
}

function RouteDataFetcherInnerChild<TData>({route, queryName, children}: RouteDataFetcherProps<TData>) {
  const {data} = useChildRouteQuery(route, queryName)
  return <>{children(data as TData | undefined)}</>
}

try{ FilesChangedRouteDataFetcher.displayName ||= 'FilesChangedRouteDataFetcher' } catch {}
try{ RouteDataFetcher.displayName ||= 'RouteDataFetcher' } catch {}
try{ RouteDataFetcherInner.displayName ||= 'RouteDataFetcherInner' } catch {}
try{ RouteDataFetcherInnerChild.displayName ||= 'RouteDataFetcherInnerChild' } catch {}